import type { IntlShape } from "react-intl";
import { showAlertDialog } from "@certa/catalyst/components/AlertDialog";
import { showToast, ToastTypes } from "@certa/catalyst/components/Toast";

type NavigateFunction = (path: string) => void;

export type WorkflowCreationError = {
  json: () => Promise<{
    object_id?: string[];
    error?: string;
    existing_workflow_id?: number;
  }>;
};

export type WorkflowCreationErrorResult = {
  errorData?: {
    errorMessage?: string;
    errorType?: string;
    existingWorkflowId?: number;
  };
};

/**
 * Handles workflow creation errors, specifically the "too_many_active_workflows" error.
 * Shows an alert dialog with options to review the existing workflow or cancel.
 *
 * @param error - The error object from the workflow creation mutation
 * @param navigate - The navigation function from react-router
 * @param intl - The internationalization object for translations
 * @returns Promise<WorkflowCreationErrorResult | undefined> - Returns whether the error was handled and the parsed error data, or undefined if parsing fails
 */
export const handleWorkflowCreationError = async (
  error: WorkflowCreationError,
  navigate: NavigateFunction,
  intl: IntlShape
): Promise<WorkflowCreationErrorResult | undefined> => {
  try {
    const responseBody = await error.json();
    const errorMessage = responseBody.object_id?.[0];
    const errorType = responseBody.error;
    const existingWorkflowId = responseBody.existing_workflow_id;

    if (errorMessage && errorType === "too_many_active_workflows") {
      showAlertDialog({
        title: intl.formatMessage({
          id: "multipleWorkflowExists.warning",
          defaultMessage: "Warning"
        }),
        children: errorMessage,
        onPrimaryAction: () => {
          navigate(`/process/${existingWorkflowId}`);
        },
        onSecondaryAction: () => {},
        primaryActionText: intl.formatMessage({
          id: "multipleWorkflowExists.reviewWorkflow",
          defaultMessage: "Review Workflow"
        }),
        secondaryActionText: intl.formatMessage({
          id: "multipleWorkflowExists.cancel",
          defaultMessage: "Cancel"
        })
      });
      return {
        errorData: { errorMessage, errorType, existingWorkflowId }
      };
    } else {
      showToast({
        title: intl.formatMessage({
          id: "errorMessageInstances.somethingWentWrong",
          defaultMessage: "Something went wrong."
        }),
        type: ToastTypes.ERROR
      });
    }

    return {
      errorData: { errorMessage, errorType, existingWorkflowId }
    };
  } catch (jsonError) {
    console.warn("Failed to parse workflow creation error:", jsonError);
    showToast({
      title: intl.formatMessage({
        id: "errorMessageInstances.somethingWentWrong",
        defaultMessage: "Something went wrong."
      }),
      type: ToastTypes.ERROR
    });
  }
};
